// Billing details a host gives so Sekretly can pay them against their invoice
// (a "recibo verde" for Portuguese freelancers). The same checks run in the
// database (is_valid_pt_nif, is_valid_iban), so a value that passes here is
// accepted there.

export interface BillingForm {
  legal_name: string;
  nif: string;
  address_line: string;
  postal_code: string;
  city: string;
  country: string;
  iban: string;
}

export const EMPTY_BILLING: BillingForm = {
  legal_name: '',
  nif: '',
  address_line: '',
  postal_code: '',
  city: '',
  country: 'PT',
  iban: '',
};

export function normalizeNif(value: string): string {
  return value.replace(/\s/g, '');
}

export function normalizeIban(value: string): string {
  return value.replace(/\s/g, '').toUpperCase();
}

/** "PT50 0002 0123 …" — groups of four, for reading and typing. */
export function formatIban(value: string): string {
  return normalizeIban(value).replace(/(.{4})/g, '$1 ').trim();
}

/** Last four characters, for showing a saved IBAN without exposing it. */
export function maskIban(value: string): string {
  const v = normalizeIban(value);
  return v.length <= 8 ? v : `${v.slice(0, 4)} •••• •••• ${v.slice(-4)}`;
}

/** Portuguese NIF: 9 digits, the last one a mod-11 check digit. */
export function isValidPtNif(value: string): boolean {
  const v = normalizeNif(value);
  if (!/^[1-9][0-9]{8}$/.test(v)) return false;
  let sum = 0;
  for (let i = 0; i < 8; i++) sum += Number(v[i]) * (9 - i);
  let check = 11 - (sum % 11);
  if (check >= 10) check = 0;
  return check === Number(v[8]);
}

/** IBAN shape plus the ISO 7064 mod-97 check; Portuguese IBANs are 25 characters. */
export function isValidIban(value: string): boolean {
  const v = normalizeIban(value);
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(v)) return false;
  if (v.startsWith('PT') && v.length !== 25) return false;
  const rearranged = v.slice(4) + v.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const digits = /[A-Z]/.test(ch) ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of digits) remainder = (remainder * 10 + Number(d)) % 97;
  }
  return remainder === 1;
}

export type BillingErrors = Partial<Record<keyof BillingForm, string>>;

/** Field-by-field problems, in the words shown under each field. Empty = valid. */
export function billingErrors(form: BillingForm): BillingErrors {
  const errors: BillingErrors = {};
  const len = (s: string) => s.trim().length;
  if (len(form.legal_name) < 2) errors.legal_name = 'Enter your full name as it appears on your recibo.';
  if (!isValidPtNif(form.nif)) errors.nif = 'This NIF doesn’t look right — it has 9 digits.';
  if (len(form.address_line) < 3) errors.address_line = 'Enter your street address.';
  if (len(form.postal_code) < 3) errors.postal_code = 'Enter your postal code.';
  if (len(form.city) < 2) errors.city = 'Enter your city.';
  if (!/^[A-Za-z]{2}$/.test(form.country.trim())) errors.country = 'Use a two-letter country code, like PT.';
  if (!isValidIban(form.iban)) errors.iban = 'This IBAN doesn’t look right — check it against your bank statement.';
  return errors;
}

export function billingComplete(form: BillingForm): boolean {
  return Object.keys(billingErrors(form)).length === 0;
}
